import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  BarChart3,
  CheckCircle,
  Clock,
  Facebook,
  Instagram,
  MessageCircle,
  MessagesSquare,
  PhoneCall,
  ShieldCheck,
  Users,
  Workflow
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';
import ContactForm from '../ContactForm';
import MultiPurposePopup from '../components/MultiPurposePopup';

const Pill = ({ icon: Icon, label, className }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${className}`}>
    <Icon size={16} />
    <span>{label}</span>
  </div>
);

const Tile = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className={`p-3 rounded-xl w-fit mb-4 ${color.bg} ${color.text}`}>
      <Icon size={24} />
    </div>
    <div className="text-lg font-bold text-gray-900 mb-2">{title}</div>
    <div className="text-sm text-gray-600 leading-relaxed">{description}</div>
  </div>
);

const CompareCard = ({ title, subtitle, items, tone, badge }) => (
  <div className={`bg-white border rounded-3xl p-8 ${tone === 'good' ? 'border-emerald-200' : 'border-gray-200'}`}>
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <div className="text-xl font-extrabold text-[#141414]">{title}</div>
        <div className="text-sm text-gray-500 mt-1">{subtitle}</div>
      </div>
      <div className={`px-3 py-1 rounded-full text-xs font-bold border ${tone === 'good' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
        {badge}
      </div>
    </div>
    <div className="space-y-3">
      {items?.map((it) => (
        <div key={it} className="flex items-start gap-3">
          <CheckCircle size={18} className={tone === 'good' ? 'text-emerald-600 mt-0.5' : 'text-gray-400 mt-0.5'} />
          <div className="text-sm text-gray-700 leading-relaxed">{it}</div>
        </div>
      ))}
    </div>
  </div>
);

const CaseCard = ({ title, before, after, kpis, beforeLabel, afterLabel }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-8">
    <div className="text-2xl font-extrabold text-[#141414]">{title}</div>
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
        <div className="text-sm font-extrabold text-gray-900">{beforeLabel}</div>
        <div className="mt-4 space-y-3">
          {before?.map((it) => (
            <div key={it} className="flex items-start gap-3 text-sm text-gray-700">
              <div className="w-2 h-2 rounded-full bg-gray-400 mt-2" />
              <div className="leading-relaxed">{it}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
        <div className="text-sm font-extrabold text-emerald-900">{afterLabel}</div>
        <div className="mt-4 space-y-3">
          {after?.map((it) => (
            <div key={it} className="flex items-start gap-3 text-sm text-emerald-900">
              <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2" />
              <div className="leading-relaxed">{it}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
      {kpis?.map((k) => (
        <div key={k.label} className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{k.label}</div>
          <div className="mt-2 text-2xl font-extrabold text-[#141414]">{k.val}</div>
          <div className="mt-1 text-sm text-gray-600">{k.cap}</div>
        </div>
      ))}
    </div>
  </div>
);

const DiagramNode = ({ icons, title, caption, variant }) => {
  const tone = variant || 'neutral';
  const wrapperClass = tone === 'dark'
    ? 'bg-[#141414] border-[#141414] text-white'
    : tone === 'good'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
      : tone === 'bad'
        ? 'bg-rose-50 border-rose-200 text-rose-900'
        : 'bg-white border-gray-200 text-gray-900';

  const iconWrapClass = tone === 'dark'
    ? 'bg-white/10 border-white/15 text-white'
    : tone === 'good'
      ? 'bg-white/60 border-emerald-200 text-emerald-700'
      : tone === 'bad'
        ? 'bg-white/60 border-rose-200 text-rose-700'
        : 'bg-gray-50 border-gray-200 text-gray-700';

  const captionClass = tone === 'dark'
    ? 'text-white/80'
    : tone === 'good'
      ? 'text-emerald-900/80'
      : tone === 'bad'
        ? 'text-rose-900/80'
        : 'text-gray-600';

  return (
    <div className={`w-full min-w-0 border rounded-2xl px-4 py-4 flex items-start gap-3 ${wrapperClass}`}>
      <div className={`p-2 rounded-xl border ${iconWrapClass}`}>
        <div className="flex items-center gap-1.5">
          {icons.map((Icon, idx) => (
            <Icon key={`${title}-${idx}`} size={18} />
          ))}
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-sm font-extrabold">{title}</div>
        <div className={`mt-1 text-xs leading-relaxed ${captionClass}`}>{caption}</div>
      </div>
    </div>
  );
};

const DiagramFlow = ({ nodes }) => (
  <div className="flex flex-col gap-3">
    {nodes.map((n, idx) => (
      <React.Fragment key={idx}>
        <DiagramNode icons={n.icons} title={n.title} caption={n.caption} variant={n.variant} />
        {idx < nodes.length - 1 && (
          <div className="flex items-center justify-center py-1">
            <ArrowDown size={18} className="text-gray-300" />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
);

const OmnicanalidadPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const heroImageUrl = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=Omnicanalidad';

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const painPoints = useMemo(() => {
    const items = t('omnicanalidad.pain_points.items', { returnObjects: true });
    const icons = [Users, ShieldCheck, BarChart3, Workflow];
    const colors = [
      { bg: 'bg-gray-50', text: 'text-gray-700' },
      { bg: 'bg-blue-50', text: 'text-blue-700' },
      { bg: 'bg-amber-50', text: 'text-amber-700' },
      { bg: 'bg-emerald-50', text: 'text-emerald-700' }
    ];
    return items?.map((it, idx) => ({
      ...it,
      icon: icons[idx],
      color: colors[idx]
    }));
  }, [t]);

  const steps = useMemo(() => {
    const items = t('omnicanalidad.how_it_works.items', { returnObjects: true });
    const icons = [MessageCircle, ShieldCheck, PhoneCall];
    return items?.map((it, idx) => ({
      ...it,
      icon: icons[idx]
    }));
  }, [t]);

  const stats = useMemo(() => {
    const labels = [
      t('omnicanalidad.stats.visibility', { defaultValue: 'Visibilidad total' }),
      t('omnicanalidad.stats.response', { defaultValue: 'Respuesta más rápida' }),
      t('omnicanalidad.stats.continuity', { defaultValue: 'Continuidad operativa' })
    ];
    const values = [
      t('omnicanalidad.stats.visibility_val', { defaultValue: 'Gerencia ve todo' }),
      t('omnicanalidad.stats.response_val', { defaultValue: 'Menos espera' }),
      t('omnicanalidad.stats.continuity_val', { defaultValue: 'Sin depender de 1 persona' })
    ];
    const icons = [BarChart3, Clock, ShieldCheck];
    return labels.map((label, idx) => ({ label, value: values[idx], icon: icons[idx] }));
  }, [t]);

  const organizedDiagram = useMemo(() => {
    const nodes = t('omnicanalidad.comparison_table.organized.nodes', { returnObjects: true });
    const icons = [[MessageCircle, PhoneCall], [Workflow], [Users], [ShieldCheck]];
    const variants = ['good', 'good', 'good', 'dark'];
    return nodes?.map((n, idx) => ({
      ...n,
      icons: icons[idx],
      variant: variants[idx],
      caption: n.desc // Maps JSON 'desc' to DiagramNode 'caption'
    }));
  }, [t]);

  const messyNodes = useMemo(() => {
    const nodes = t('omnicanalidad.comparison_table.messy.nodes', { returnObjects: true });
    const icons = [[MessageCircle], [MessageCircle], [PhoneCall], [Users], [AlertTriangle], [Workflow]];
    const variants = ['bad', 'bad', 'bad', 'bad', 'bad', 'bad'];
    return nodes?.map((n, idx) => ({
      ...n,
      icons: icons[idx],
      variant: variants[idx],
      caption: n.desc
    }));
  }, [t]);

  const cases = useMemo(() => t('omnicanalidad.cases.items', { returnObjects: true }), [t]);

  return (
    <div className="relative min-h-screen bg-transparent">
      <PageHeader onOpenPopup={openPopup} />
      
      <MultiPurposePopup
        isOpen={isPopupOpen}
        onClose={closePopup}
        title={t('omnicanalidad.popup_title', { defaultValue: 'Solicitar Diagnóstico' })}
        description={t('omnicanalidad.popup_desc', { defaultValue: 'Complete el formulario y le contactamos para coordinar la llamada.' })}
      >
        <ContactForm
          formData="inline/4/10oz6w"
          formId="4"
          scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
        />
      </MultiPurposePopup>

      <main className="pb-20">
        <section className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-20">
          <div className="relative overflow-hidden bg-[#0B0B0B] min-h-screen">
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate('/');
                  }}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition font-semibold bg-white/10 border border-white/15 backdrop-blur px-4 py-2 rounded-full"
                >
                  <ArrowLeft size={16} />
                  {t('common.back')}
                </button>
              </div>
            </div>

            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt="Omnicanalidad"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=Omnicanalidad'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 p-8 md:p-14 min-h-screen flex flex-col justify-end max-w-7xl mx-auto">
              <div className="flex flex-wrap gap-2 mb-6">
                <Pill icon={MessageCircle} label="WhatsApp Business API" className="bg-white/10 text-white border-white/15" />
                <Pill icon={PhoneCall} label="Telefonía" className="bg-white/10 text-white border-white/15" />
                <Pill icon={Facebook} label="Facebook" className="bg-white/10 text-white border-white/15" />
                <Pill icon={Instagram} label="Instagram" className="bg-white/10 text-white border-white/15" />
                <Pill icon={MessagesSquare} label={t('header.live_chat', { defaultValue: 'Chat en vivo' })} className="bg-white/10 text-white border-white/15" />
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight max-w-5xl">
                {t('omnicanalidad.hero.title')}
              </h1>

              <p className="mt-6 text-xl md:text-2xl text-white/90 max-w-4xl font-medium">
                {t('omnicanalidad.hero.subtitle')}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openPopup}
                  className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('omnicanalidad.hero.cta_demo')}
                </button>
                <a
                  href="#como-funciona"
                  className="bg-transparent border border-white/40 text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition text-center"
                >
                  {t('omnicanalidad.hero.cta_how')}
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <section className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('omnicanalidad.pain_points.title')}</h2>
                <p className="text-gray-600 max-w-4xl mx-auto">
                  {t('omnicanalidad.pain_points.subtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
                {painPoints?.map((it) => (
                  <Tile
                    key={it.title}
                    icon={it.icon}
                    title={it.title}
                    description={it.desc}
                    color={it.color}
                  />
                ))}
              </div>
            </section>

            <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                {stats.map((b) => (
                  <div key={b.label} className="bg-white border border-gray-200 rounded-3xl p-7 h-full">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-700">
                        <b.icon size={24} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-500">{b.label}</div>
                        <div className="text-xl font-extrabold text-[#141414]">{b.value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10 bg-white border border-gray-200 rounded-3xl p-8">
                <div className="text-sm font-semibold uppercase tracking-widest text-gray-500">{t('omnicanalidad.misunderstanding.label')}</div>
                <div className="mt-3 text-2xl md:text-3xl font-extrabold text-[#141414]">
                  {t('omnicanalidad.misunderstanding.title')}
                </div>
                <div className="mt-4 text-gray-600 leading-relaxed max-w-4xl">
                  {t('omnicanalidad.misunderstanding.desc')}
                </div>
              </div>
            </section>

            <section id="como-funciona" className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('omnicanalidad.how_it_works.title')}</h2>
                <p className="text-gray-600 max-w-4xl mx-auto">
                  {t('omnicanalidad.how_it_works.subtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
                {steps?.map((s) => (
                  <div key={s.title} className="bg-white border border-gray-200 rounded-3xl p-8 h-full">
                    <div className="p-3 rounded-xl w-fit mb-5 bg-indigo-50 text-indigo-700">
                      <s.icon size={24} />
                    </div>
                    <div className="text-xl font-extrabold text-gray-900">{s.title}</div>
                    <div className="mt-3 text-sm text-gray-600 leading-relaxed">{s.desc}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('omnicanalidad.comparison_table.title')}</h2>
                <p className="text-gray-600 max-w-4xl mx-auto">
                  {t('omnicanalidad.comparison_table.subtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-8 h-full">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xl font-extrabold text-emerald-900">{t('omnicanalidad.comparison_table.organized.title')}</div>
                      <div className="mt-2 text-sm text-emerald-900/80 leading-relaxed">
                        {t('omnicanalidad.comparison_table.organized.desc')}
                      </div>
                    </div>
                    <div className="px-3 py-1 rounded-full text-xs font-bold border bg-white/60 border-emerald-200 text-emerald-800">
                      {t('omnicanalidad.comparison_table.organized.badge')}
                    </div>
                  </div>
                  <div className="mt-6">
                    <DiagramFlow nodes={organizedDiagram || []} />
                  </div>
                </div>

                <div className="bg-rose-50 border border-rose-200 rounded-3xl p-8 h-full">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xl font-extrabold text-rose-900">{t('omnicanalidad.comparison_table.messy.title')}</div>
                      <div className="mt-2 text-sm text-rose-900/80 leading-relaxed">
                        {t('omnicanalidad.comparison_table.messy.desc')}
                      </div>
                    </div>
                    <div className="px-3 py-1 rounded-full text-xs font-bold border bg-white/60 border-rose-200 text-rose-800">
                      {t('omnicanalidad.comparison_table.messy.badge')}
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="transform rotate-1">
                      <DiagramNode icons={messyNodes?.[0]?.icons || []} title={messyNodes?.[0]?.title} caption={messyNodes?.[0]?.caption} variant={messyNodes?.[0]?.variant} />
                    </div>
                    <div className="transform -rotate-1">
                      <DiagramNode icons={messyNodes?.[1]?.icons || []} title={messyNodes?.[1]?.title} caption={messyNodes?.[1]?.caption} variant={messyNodes?.[1]?.variant} />
                    </div>
                    <div className="sm:col-span-2 transform rotate-1">
                      <DiagramNode icons={messyNodes?.[2]?.icons || []} title={messyNodes?.[2]?.title} caption={messyNodes?.[2]?.caption} variant={messyNodes?.[2]?.variant} />
                    </div>
                  </div>

                  <div className="flex items-center justify-center py-2">
                    <ArrowDown size={18} className="text-rose-300" />
                  </div>

                  <DiagramNode icons={messyNodes?.[3]?.icons || []} title={messyNodes?.[3]?.title} caption={messyNodes?.[3]?.caption} variant={messyNodes?.[3]?.variant} />

                  <div className="flex items-center justify-center py-2">
                    <ArrowDown size={18} className="text-rose-300" />
                  </div>

                  <DiagramNode icons={messyNodes?.[4]?.icons || []} title={messyNodes?.[4]?.title} caption={messyNodes?.[4]?.caption} variant={messyNodes?.[4]?.variant} />

                  <div className="flex items-center justify-center py-2">
                    <ArrowDown size={18} className="text-rose-300" />
                  </div>

                  <DiagramNode icons={messyNodes?.[5]?.icons || []} title={messyNodes?.[5]?.title} caption={messyNodes?.[5]?.caption} variant={messyNodes?.[5]?.variant} />
                </div>
              </div>
            </section>

            <section className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('omnicanalidad.model_comparison.title')}</h2>
                <p className="text-gray-600 max-w-4xl mx-auto">
                  {t('omnicanalidad.model_comparison.subtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <CompareCard
                  title={t('omnicanalidad.model_comparison.personal.title')}
                  subtitle={t('omnicanalidad.model_comparison.personal.subtitle')}
                  items={t('omnicanalidad.model_comparison.personal.items', { returnObjects: true })}
                  tone="bad"
                  badge={t('omnicanalidad.model_comparison.personal.badge')}
                />
                <CompareCard
                  title={t('omnicanalidad.model_comparison.centralized.title')}
                  subtitle={t('omnicanalidad.model_comparison.centralized.subtitle')}
                  items={t('omnicanalidad.model_comparison.centralized.items', { returnObjects: true })}
                  tone="good"
                  badge={t('omnicanalidad.model_comparison.centralized.badge')}
                />
              </div>
            </section>

            <section className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('omnicanalidad.cases.title')}</h2>
                <p className="text-gray-600 max-w-4xl mx-auto">
                  {t('omnicanalidad.cases.subtitle')}
                </p>
              </div>
              <div className="space-y-10">
                {cases?.map((c) => (
                  <CaseCard 
                    key={c.title} 
                    title={c.title} 
                    before={c.before} 
                    after={c.after} 
                    kpis={c.kpis} 
                    beforeLabel={t('common.before', { defaultValue: 'Antes' })}
                    afterLabel={t('common.after', { defaultValue: 'Después' })}
                  />
                ))}
              </div>
            </section>

            <section className="mb-24 bg-[#0B0B0B] rounded-3xl p-10 md:p-14 text-white overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/25 via-transparent to-emerald-500/20" />
              <div className="relative z-10 text-center max-w-4xl mx-auto">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur text-sm font-semibold">
                  <CheckCircle size={18} />
                  {t('omnicanalidad.final_cta.badge')}
                </div>
                <h2 className="mt-6 text-3xl md:text-4xl font-extrabold">{t('omnicanalidad.final_cta.title')}</h2>
                <p className="mt-4 text-white/80 leading-relaxed">
                  {t('omnicanalidad.final_cta.desc')}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={openPopup}
                    className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition"
                  >
                    {t('omnicanalidad.final_cta.btn_demo')}
                  </button>
                  <a
                    href="/whatsapp-api"
                    className="bg-transparent border border-white/40 text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition text-center"
                  >
                    {t('omnicanalidad.final_cta.btn_api')}
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OmnicanalidadPage;

